import { Link } from "react-router-dom";
import { Check, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SERVICES } from "@/lib/content";

const TEAM = [
  {
    name: "Dr. Ananth Kulkarni",
    role: "Founder · Implantologist",
    qual: "BDS, MDS (Prosthodontics), Fellowship in Oral Implantology",
    bio: "Twelve years of chairside experience with a special interest in full-mouth rehabilitation and single-sitting root canals.",
  },
  {
    name: "Dr. Meera Joshi",
    role: "Orthodontist",
    qual: "BDS, MDS (Orthodontics & Dentofacial Orthopaedics)",
    bio: "Handles braces and clear aligner cases for children and adults, with a calm, unhurried approach to first visits.",
  },
  {
    name: "Dr. Sameer Rao",
    role: "Oral Surgeon · Visiting",
    qual: "BDS, MDS (Oral & Maxillofacial Surgery)",
    bio: "Available on scheduled days for wisdom tooth removal, minor surgical procedures and complex extractions.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-20 md:py-24">
          <p className="text-sm tracking-[0.18em] uppercase text-sky-600">About Nidaan</p>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl tracking-tight text-slate-900 max-w-3xl leading-[1.05]">
            A small clinic with unhurried appointments
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-2xl leading-relaxed">
            Nidaan Dental Care was started with one simple rule: never rush a patient. We keep our daily
            appointment count deliberately low so every person gets a full explanation, a written estimate and
            time to ask questions before anything begins.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-8 py-24">
        <h2 className="text-3xl sm:text-4xl tracking-tight text-slate-900">Meet the team</h2>
        <div className="mt-12 grid gap-6 md:grid-cols-3" data-testid="team-grid">
          {TEAM.map((t) => (
            <article
              key={t.name}
              data-testid={`team-card-${t.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="bg-white border border-slate-100 rounded-2xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] transition-transform transition-shadow duration-300"
            >
              <span className="grid place-items-center w-14 h-14 rounded-full bg-sky-50 text-blue-900 font-display text-xl">
                {t.name.split(" ")[1]?.[0] || "N"}
              </span>
              <h3 className="mt-6 text-xl font-medium text-slate-900">{t.name}</h3>
              <p className="text-sm text-sky-600 mt-1">{t.role}</p>
              <p className="mt-4 text-xs text-slate-500 flex gap-2">
                <GraduationCap className="w-4 h-4 shrink-0" />
                {t.qual}
              </p>
              <p className="mt-4 text-sm text-slate-600 leading-relaxed">{t.bio}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-24">
          <p className="text-sm tracking-[0.18em] uppercase text-sky-600">Services</p>
          <h2 className="mt-4 text-3xl sm:text-4xl tracking-tight text-slate-900">
            Everything we offer, in plain language
          </h2>
          <div className="mt-14 grid gap-6 md:grid-cols-2" data-testid="services-list">
            {SERVICES.map((s) => (
              <div
                key={s.key}
                data-testid={`service-detail-${s.key}`}
                className="border border-slate-100 rounded-2xl p-8 bg-slate-50/60"
              >
                <h3 className="text-xl font-medium text-slate-900">{s.title}</h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
                <ul className="mt-6 grid sm:grid-cols-2 gap-2">
                  {s.items.map((i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                      <Check className="w-4 h-4 text-sky-500 mt-0.5 shrink-0" />
                      {i}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-14">
            <Link to="/book">
              <Button
                className="rounded-full h-12 px-8 bg-blue-900 text-white hover:bg-blue-950 hover:text-white"
                data-testid="about-book-button"
              >
                Book an Appointment
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
